import { useContext, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { AuthContext, MyContext } from '../context/AuthContext';

/**
 * Picks up a pending referral code stored in localStorage (set on /signup
 * via ?ref=CODE) and claims it once the user is authenticated + has a
 * user record. Idempotent — the RPC no-ops if the user already has
 * referred_by set. On success, clears the pending code and fires a
 * toast via a custom event so any listener (e.g. a global snackbar)
 * can react.
 */
export const REFERRAL_STORAGE_KEY = 'wipe_ref';

export function useReferralClaim() {
  const userRecord = useContext<MyContext>(AuthContext).userRecord;
  const claimedRef = useRef(false);

  useEffect(() => {
    if (!userRecord || claimedRef.current) return;
    const code = localStorage.getItem(REFERRAL_STORAGE_KEY);
    if (!code) return;

    // If this user already has a referrer recorded, just clean up.
    if (userRecord.referred_by) {
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
      claimedRef.current = true;
      return;
    }

    claimedRef.current = true;
    supabase.rpc('claim_referral', { p_code: code }).then(({ data, error }) => {
      if (error) {
        console.warn('Referral claim failed:', error.message);
        // Don't retry automatically — the code may be invalid. Clear it
        // so we don't keep pestering the RPC on every page render.
        localStorage.removeItem(REFERRAL_STORAGE_KEY);
        return;
      }
      localStorage.removeItem(REFERRAL_STORAGE_KEY);
      if (data) {
        window.dispatchEvent(
          new CustomEvent('wipe-referral-claimed', {
            detail: { bonusSeconds: 7 * 86400 },
          }),
        );
      }
    });
  }, [userRecord?.id, userRecord?.referred_by]); // eslint-disable-line react-hooks/exhaustive-deps
}
